import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, LoaderCircleIcon, SendHorizontalIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { NisoloSubmission, SizeTier } from '../hooks/useNisoloSubmissions';
import { NisoloOrderData } from '../hooks/useNisoloOrders';
import { LineOutcome, reconcileSubmission } from '../utils/submissionOutcome';
import { askCarli, AgentMessage } from '../utils/carliAgent';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const compact = (value: number) => Math.abs(value) >= 1000 ? `${value < 0 ? '−' : ''}$${Math.round(Math.abs(value) / 1000).toLocaleString('en-US')}K` : currency(value);
const perUnit = (value: number) => `$${value.toFixed(2)}`;
const count = (value: number) => value.toLocaleString('en-US');
const signedPct = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
export function UploadAIInsights({
  uploads,
  orders,
  loading,
  error
}: {
  uploads: NisoloSubmission[];
  orders: NisoloOrderData | null;
  loading: boolean;
  error: string | null;
}) {
  const [openUpload, setOpenUpload] = useState<string | null>(null);
  if (loading) {
    return <article className="flex items-center justify-center gap-2 rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-sm text-ink/55" data-id="element-2045" data-mp="ezsqbj8hmfb5">
        <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2046" data-mp="3ifzjuwx6uw67" />
        Carli is reading the receiving file…
      </article>;
  }
  if (error) {
    return <article className="rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-center text-sm text-ink/55" data-id="element-2047" data-mp="3rge5xyhz8far">{error}</article>;
  }
  if (uploads.length === 0) {
    return <article className="rounded-2xl border border-ink/10 bg-paper p-8 text-center" aria-labelledby="ai-insights-empty" data-id="element-2048" data-mp="80bd8clv39y">
        <h2 id="ai-insights-empty" className="text-base font-semibold" data-id="element-2049" data-mp="1bp9s3n4cnh7k">AI insights</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-2050" data-mp="1br329ce68jkb">Select an inventory submission above to see Carli’s read on what she predicted and what actually happened.</p>
      </article>;
  }
  return <div className="space-y-4" data-id="element-2051" data-mp="3hr4453aqpw1j">
      {uploads.map(upload => <InsightReport key={upload.id} upload={upload} orders={orders} open={openUpload === upload.id} onToggle={() => setOpenUpload(current => current === upload.id ? null : upload.id)} data-id="element-2052" data-mp="3hohtuvwgt2fm" />)}
    </div>;
}
function InsightReport({
  upload,
  orders,
  open,
  onToggle
}: {
  upload: NisoloSubmission;
  orders: NisoloOrderData | null;
  open: boolean;
  onToggle: () => void;
}) {
  const outcome = useMemo(() => reconcileSubmission(upload, orders), [upload, orders]);
  const weightedMsrp = upload.received > 0 ? upload.msrp / upload.received : 0;
  const coreShare = upload.received > 0 ? Math.round(upload.sizes.filter(size => size.tier === 'Core').reduce((sum, size) => sum + size.units, 0) / upload.received * 100) : 0;
  const headlineTone = outcome.matched ? outcome.variance >= 0 ? 'text-forest' : 'text-[#94452E]' : 'text-ink';
  return <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" data-id="element-2053" data-mp="7tlnjjvpjjjk">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-cream/45 sm:px-6" data-id="element-2054" data-mp="7rsjmcchbdf9">
        <span className="flex min-w-0 items-center gap-3" data-id="element-2055" data-mp="14ke7islrhvqm">
          <CarliMark data-id="element-2056" data-mp="14hryspn4xa6t" />
          <span className="min-w-0" data-id="element-2057" data-mp="qkd7ffdbmsc7">
            <span className="block text-base font-semibold text-ink" data-id="element-2058" data-mp="qjbjiab7ibd2">AI insights · {upload.name}</span>
            <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2059" data-mp="vhv68cxmjr6h">
              {upload.grade} · {count(upload.received)} units · predicted {compact(upload.estimatedRecovery)}
              {outcome.matched && ` · actual ${compact(outcome.actualSales)} to date`}
            </span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3" data-id="element-2060" data-mp="3vkmxzjxohcop">
          {outcome.matched ? <span className={`hidden items-center gap-1 text-xs font-semibold sm:flex ${headlineTone}`} data-id="element-2061" data-mp="3qqsiksp3vjvf">
              {outcome.variance >= 0 ? <TrendingUpIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2062" data-mp="3lqwd0p0rfkdr" /> : <TrendingDownIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2063" data-mp="3hsyl3efluk6o" />}
              {signedPct(outcome.variancePct)} vs predicted
            </span> : <span className="hidden rounded-full bg-ink/8 px-2.5 py-1 text-[11px] font-semibold text-ink/55 sm:inline" data-id="element-2064" data-mp="dd7ajky1koap">Awaiting first sales</span>}
          <ChevronDownIcon className={`h-4 w-4 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2065" data-mp="9dyeg9fj4ebz" />
        </span>
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-6 sm:px-6" data-id="element-2066" data-mp="2tzzzrnasfdpy">
          <Scoreboard upload={upload} outcome={outcome} data-id="element-2067" data-mp="2p26vizt1otsb" />

          <CarliNarrative upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2068" data-mp="14l7gj84wqdi9" />

          {outcome.matched && (outcome.overPerformers.length > 0 || outcome.underPerformers.length > 0) && <section className="mt-4 grid gap-3 lg:grid-cols-2" aria-label="Where the model was right and wrong" data-id="element-2069" data-mp="1dldo6ddxcj9i">
              <VarianceList title="Beat the prediction" tone="up" items={outcome.overPerformers} empty="Nothing has cleared above its estimate yet." data-id="element-2070" data-mp="1id5jf180zd5z" />
              <VarianceList title="Missed the prediction" tone="down" items={outcome.underPerformers} empty="Nothing has cleared below its estimate yet." data-id="element-2071" data-mp="1scmmrbzt1eys" />
            </section>}

          <RecoveryFactors upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2072" data-mp="2wn516i4eh7j" />

          <Composition upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2073" data-mp="6xp7rxb8xbe9" />

          <ReportChat upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2074" data-mp="1a8ct28w0l64b" />
        </div>}
    </article>;
}
function Scoreboard({
  upload,
  outcome
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
}) {
  const positive = outcome.variance >= 0;
  return <section aria-label="Predicted versus actual" data-id="element-2075" data-mp="1f9jyw2eljv8c">
      <div className="grid gap-3 lg:grid-cols-[1.1fr_1.1fr_0.8fr]" data-id="element-2076" data-mp="1t5nrxsdqhxj8">
        <article className="rounded-xl border border-ink/10 bg-cream/45 p-5" data-id="element-2077" data-mp="1o8dtxoca4djs">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2078" data-mp="22ws6iqzhzgdj">Predicted recovery</p>
          <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2079" data-mp="1xxp47eawjo99">{currency(upload.estimatedRecovery)}</p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2080" data-mp="2byhxxwtdqfbb">{perUnit(outcome.predictedAur)} per unit across {count(upload.received)} units</p>
        </article>

        <article className={`rounded-xl border p-5 ${outcome.matched ? positive ? 'border-forest/30 bg-forest/6' : 'border-[#BB5E40]/30 bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/45'}`} data-id="element-2081" data-mp="26wiruf6994ao">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2082" data-mp="2vh5oxv49aqmc">Actual recovery to date</p>
          <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${outcome.matched ? positive ? 'text-forest' : 'text-[#94452E]' : 'text-ink/40'}`} data-id="element-2083" data-mp="2hlti4pctt9p2">
            {outcome.matched ? currency(outcome.actualSales) : '—'}
          </p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2084" data-mp="2clfkm18upi5a">
            {outcome.matched ? `${perUnit(outcome.actualAur)} per unit across ${count(outcome.soldUnits)} units sold` : 'No matched sales recorded against this upload yet'}
          </p>
        </article>

        <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2085" data-mp="1oujlsk5z9moj">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2086" data-mp="1ttwh7tax022q">Variance on sold units</p>
          {outcome.matched ? <>
              <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${positive ? 'text-forest' : 'text-[#94452E]'}`} data-id="element-2087" data-mp="29b4m8ppuhei2">{signedPct(outcome.variancePct)}</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2088" data-mp="1zcotkib65x6g">{compact(outcome.variance)} against {currency(outcome.predictedForSold)} predicted for those units</p>
            </> : <>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink/40" data-id="element-2089" data-mp="1qe3ckrlciq7s">—</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2090" data-mp="1lljpn40rqiqb">Variance appears once units from this upload sell</p>
            </>}
        </article>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-2091" data-mp="1bofes0cdi9ot">
        <MiniStat label="Sell-through" value={`${outcome.sellThrough.toFixed(1)}%`} detail={`${count(outcome.soldUnits)} of ${count(upload.received)} units`} data-id="element-2092" data-mp="1bpytvnmamep0" />
        <MiniStat label="Units remaining" value={count(outcome.unsoldUnits)} detail={`${compact(outcome.remainingPredicted)} still forecast`} data-id="element-2093" data-mp="12n7ejcqaqys3" />
        <MiniStat label="AUR vs forecast" value={outcome.matched ? signedPct(outcome.aurVariancePct) : '—'} detail={outcome.matched ? `${perUnit(outcome.actualAur)} actual vs ${perUnit(outcome.predictedAur)} predicted` : `${perUnit(outcome.predictedAur)} predicted`} tone={outcome.matched ? outcome.aurVariancePct >= 0 ? 'up' : 'down' : undefined} data-id="element-2094" data-mp="3eajdkqe3bgzu" />
      </div>

      <div className="mt-3" data-id="element-2095" data-mp="2vii4kv7lqacp">
        <div className="flex items-center justify-between text-xs text-ink/55" data-id="element-2096" data-mp="2zles90c00wni">
          <span data-id="element-2097" data-mp="34kzrlbv2z70r">Recovered {compact(outcome.actualSales)}</span>
          <span data-id="element-2098" data-mp="34jgdnrwwd825">Forecast {compact(upload.estimatedRecovery)}</span>
        </div>
        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-ink/8" role="img" aria-label={`${Math.round(outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}% of forecast recovered`} data-id="element-2099" data-mp="39crhrlabraqd">
          <div className="h-full rounded-full bg-[#BB5E40]" style={{
          width: `${Math.min(100, outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}%`
        }} data-id="element-2100" data-mp="2gu41qka6qlw5" />
        </div>
      </div>
    </section>;
}
function MiniStat({
  label,
  value,
  detail,
  tone
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'up' | 'down';
}) {
  return <article className="rounded-xl border border-ink/10 bg-paper px-4 py-3.5" data-id="element-2101" data-mp="2yfx74j7ncs96">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2102" data-mp="2ydkp661w2hw4">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${tone === 'up' ? 'text-forest' : tone === 'down' ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2103" data-mp="20q3lq4qwrdc2">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2104" data-mp="2aorppzm25rwr">{detail}</p>
    </article>;
}
function VarianceList({
  title,
  tone,
  items,
  empty
}: {
  title: string;
  tone: 'up' | 'down';
  items: LineOutcome[];
  empty: string;
}) {
  const up = tone === 'up';
  return <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2105" data-mp="1n0i879vvnqbf">
      <div className="flex items-center gap-2" data-id="element-2106" data-mp="1myoz7nxsku09">
        {up ? <TrendingUpIcon className="h-4 w-4 text-forest" aria-hidden="true" data-id="element-2107" data-mp="1fg7m77qpix2j" /> : <TrendingDownIcon className="h-4 w-4 text-[#94452E]" aria-hidden="true" data-id="element-2108" data-mp="1aiflbo8340m7" />}
        <h4 className="text-sm font-semibold" data-id="element-2109" data-mp="1agbf2zmpqpir">{title}</h4>
      </div>
      {items.length === 0 ? <p className="mt-3 text-xs text-ink/50" data-id="element-2110" data-mp="10k03ycyv05pw">{empty}</p> : <ul className="mt-3 space-y-2.5" data-id="element-2111" data-mp="10jqd6n5zpuj5">
          {items.map(item => <li key={item.name} className="flex items-center justify-between gap-3 border-b border-ink/8 pb-2.5 last:border-0 last:pb-0" data-id="element-2112" data-mp="wj6gfkpbwsxj">
              <span className="min-w-0" data-id="element-2113" data-mp="rptrtakngm4s">
                <span className="block truncate text-xs font-medium text-ink" data-id="element-2114" data-mp="mrsbgxelz0x0">{item.name}</span>
                <span className="text-[11px] text-ink/50" data-id="element-2115" data-mp="hu9xutpnbxio">
                  {count(item.soldUnits)} of {count(item.receivedUnits)} sold · {perUnit(item.actualAur)} actual vs {perUnit(item.predict)} predicted
                </span>
              </span>
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${up ? 'text-forest' : 'text-[#94452E]'}`} data-id="element-2116" data-mp="8uwxqrnewlam">
                {item.variance >= 0 ? '+' : '−'}{currency(Math.abs(item.variance))}
              </span>
            </li>)}
        </ul>}
    </article>;
}
function CarliNarrative({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSummary('');
    setFailed('');
    askCarli({
      messages: [{
        role: 'user',
        content: 'Give me your read on this upload: what you predicted, what actually happened, why, and what to do next.'
      }],
      context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Reply with exactly three lines, each starting with the label and a colon, and nothing else:', 'CALL: two sentences on what the model predicted for this lot and the reasoning behind that number.', 'RESULT: two sentences on what actually happened against that prediction, citing the specific styles, sizes, or price points responsible.', 'NEXT: two sentences on the single highest-value action to take now, stated concretely with the units or dollars involved.', 'Write the full sentences on the same line as the label. Do not add preamble, bullet points, markdown, or any other headings.']),
      maxTokens: 700
    }).then(reply => {
      if (!cancelled) setSummary(reply);
    }).catch((caught: Error) => {
      if (!cancelled) setFailed(caught.message);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [upload, outcome, weightedMsrp, coreShare]);
  const sections = useMemo(() => {
    if (!summary) return [];
    const order = ['CALL', 'RESULT', 'NEXT'];
    const positions = order.map(label => ({
      label,
      index: summary.search(new RegExp(`\\b${label}\\s*:`, 'i'))
    })).filter(entry => entry.index >= 0).sort((a, b) => a.index - b.index);
    return positions.map((entry, position) => {
      const next = positions[position + 1];
      const slice = summary.slice(entry.index, next ? next.index : undefined);
      return {
        label: entry.label,
        text: slice.replace(new RegExp(`^\\s*${entry.label}\\s*:\\s*`, 'i'), '').replace(/[*_#]/g, '').trim()
      };
    }).filter(section => section.text.length > 0);
  }, [summary]);
  const labels: Record<string, string> = {
    CALL: 'The call',
    RESULT: 'The result',
    NEXT: 'What to do next'
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/25 bg-[#BB5E40]/5 p-5" aria-label="Carli’s read" data-id="element-2117" data-mp="1jl33iijxwk02">
      <div className="flex items-center gap-2.5" data-id="element-2118" data-mp="1npau6l9yka0x">
        <CarliMark data-id="element-2119" data-mp="1smjdip6uek3r" />
        <span data-id="element-2120" data-mp="1sm1an9pod7mv">
          <span className="block text-sm font-semibold" data-id="element-2121" data-mp="1snkoyurrt8m0">Carli’s read</span>
          <span className="block text-xs text-ink/50" data-id="element-2122" data-mp="3d5d61a3x9q7v">{upload.name} · {upload.grade}</span>
        </span>
      </div>

      {loading && <p className="mt-3 flex items-center gap-2 text-xs text-ink/55" data-id="element-2123" data-mp="3847br7um5uw8">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2124" data-mp="3456tprjymwaa" />
          Comparing the forecast against actual sales…
        </p>}

      {failed && !loading && <p className="mt-3 text-xs leading-relaxed text-ink/60" data-id="element-2125" data-mp="3vu9x7540w2lv">{failed}</p>}

      {sections.length > 0 && <dl className="mt-3 grid gap-4 lg:grid-cols-3" data-id="element-2126" data-mp="3rv15e5sjmesy">
          {sections.map(section => <div key={section.label} data-id="element-2127" data-mp="3n1q6qrxvx4c4">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2128" data-mp="3n0oiflruylcg">{labels[section.label.toUpperCase()] ?? section.label}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-ink/75" data-id="element-2129" data-mp="jezjsl8bz2b9">{section.text}</dd>
            </div>)}
        </dl>}

      {!loading && !failed && sections.length === 0 && summary && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/75" data-id="element-2130" data-mp="xjzxyli4dt2s">{summary}</p>}
    </section>;
}
type Strength = 'Strong' | 'Moderate' | 'Limited';
const strengthTone: Record<Strength, string> = {
  Strong: 'bg-forest/12 text-forest',
  Moderate: 'bg-[#C9A04E]/20 text-[#8B6A20]',
  Limited: 'bg-[#BB5E40]/14 text-[#94452E]'
};
function RecoveryFactors({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  const topCategory = upload.categories[0];
  const categoryShare = upload.received > 0 && topCategory ? Math.round(topCategory.units / upload.received * 100) : 0;
  const deepLines = upload.lines.filter(line => line.units >= 100).length;
  const premiumUnits = upload.priceBands.filter(band => band.label === '$150 – $199' || band.label === '$200+').reduce((sum, band) => sum + band.units, 0);
  const premiumShare = upload.received > 0 ? Math.round(premiumUnits / upload.received * 100) : 0;
  const unsized = upload.sizes.find(size => size.label === 'UNSIZED' || size.label === 'Unsized')?.units ?? 0;
  const marketing = outcome.predictedAur * upload.received * 0.1;
  const dataGaps = upload.missingMsrp + upload.missingPredict + upload.missingUpc;
  const factors: Array<{
    title: string;
    strength: Strength;
    headline: string;
    points: string[];
    takeaway: string;
  }> = [{
    title: 'Pricing position',
    strength: outcome.matched ? outcome.aurVariancePct >= 0 ? 'Strong' : 'Moderate' : 'Moderate',
    headline: outcome.matched ? `Units are clearing at ${perUnit(outcome.actualAur)}, ${signedPct(outcome.aurVariancePct)} against the ${perUnit(outcome.predictedAur)} forecast.` : `The lot is modeled to clear at ${perUnit(outcome.predictedAur)}, ${recoveryRate}% of submitted MSRP.`,
    points: [`${perUnit(weightedMsrp)} weighted MSRP per unit`, `${perUnit(weightedMsrp * 0.7)} A-grade promotional price at ~30% off`, outcome.matched ? `${perUnit(outcome.actualAur)} realized on ${count(outcome.soldUnits)} units` : `${perUnit(outcome.predictedAur)} predicted recovery per unit`],
    takeaway: outcome.matched && outcome.aurVariancePct >= 0 ? 'Prices are holding above plan — hold the ladder rather than discounting.' : 'Price sits well below first-quality, which protects the brand but caps recovery.'
  }, {
    title: 'Product mix',
    strength: categoryShare >= 55 ? 'Limited' : categoryShare >= 35 ? 'Moderate' : 'Strong',
    headline: topCategory ? `${topCategory.label} carries ${categoryShare}% of units, setting the pace for the whole lot.` : 'Category mix is spread evenly across the assortment.',
    points: [`${count(upload.lineItems)} variant lines across ${count(upload.categories.length)} categories`, `${count(deepLines)} lines carry 100+ units`, upload.categories.slice(1, 3).map(item => `${item.label} ${count(item.units)}`).join(' · ') || 'No secondary category depth'],
    takeaway: categoryShare >= 55 ? 'Concentration means one category decides the outcome — merchandise it deliberately.' : 'A balanced mix gives multiple paths to recovery if one category stalls.'
  }, {
    title: 'Size distribution',
    strength: coreShare >= 55 ? 'Strong' : coreShare >= 35 ? 'Moderate' : 'Limited',
    headline: `${coreShare}% of units sit in core-demand sizes, which drive the fastest sell-through.`,
    points: [upload.sizes.filter(size => size.tier === 'Core').slice(0, 3).map(size => `${size.label} (${count(size.units)})`).join(' · ') || 'No core sizes identified', `${count(upload.sizes.filter(size => size.tier === 'Tail').reduce((sum, size) => sum + size.units, 0))} units in tail sizes`, unsized > 0 ? `${count(unsized)} units without a readable size` : 'Every unit carries a readable size'],
    takeaway: coreShare >= 50 ? 'The size curve favors liquidity — lead with core runs.' : 'Tail sizes need more time or markdown to clear.'
  }, {
    title: 'Price architecture',
    strength: premiumShare >= 45 ? 'Strong' : premiumShare >= 20 ? 'Moderate' : 'Limited',
    headline: `${premiumShare}% of units carry MSRP of $150 or higher, where recovery per unit is strongest.`,
    points: upload.priceBands.slice(0, 3).map(band => `${band.label}: ${count(band.units)} units`),
    takeaway: premiumShare >= 40 ? 'Higher retail price points give room to discount without eroding recovery.' : 'A lower-priced lot means volume, not price, has to carry recovery.'
  }, {
    title: 'Promotion potential',
    strength: marketing >= 20000 ? 'Strong' : marketing >= 5000 ? 'Moderate' : 'Limited',
    headline: `Projected recovery supports roughly ${compact(marketing)} of marketing at the standard 10% allocation.`,
    points: [`${currency(upload.estimatedRecovery)} projected gross recovery`, `${compact(marketing)} available for channel and content push`, outcome.matched ? `${count(outcome.unsoldUnits)} units still to place` : `${count(upload.received)} units awaiting first placement`],
    takeaway: 'Spend is best concentrated on the categories already converting.'
  }, {
    title: 'Data integrity',
    strength: dataGaps === 0 ? 'Strong' : dataGaps < upload.lineItems * 0.1 ? 'Moderate' : 'Limited',
    headline: dataGaps === 0 ? 'Every line carries MSRP, barcode, and a per-unit estimate.' : `${count(dataGaps)} field gaps across ${count(upload.lineItems)} lines widen the projection range.`,
    points: [`${count(upload.lineItems - upload.missingUpc)} of ${count(upload.lineItems)} lines have a barcode`, `${count(upload.lineItems - upload.missingMsrp)} of ${count(upload.lineItems)} lines have MSRP`, `${count(upload.shortShipped)} invoiced units never arrived`],
    takeaway: dataGaps === 0 ? 'Clean identifiers let us match sales back to this upload precisely.' : 'Gaps mean some sales cannot be attributed back to this lot.'
  }];
  return <section className="mt-4" aria-label="Recovery factors" data-id="element-2131" data-mp="3l7kaxvogkh32">
      <h3 className="text-sm font-semibold" data-id="element-2132" data-mp="3l60vu8ejgc2v">What is driving this lot</h3>
      <div className="mt-3 grid gap-3 lg:grid-cols-3" data-id="element-2133" data-mp="3gabubziykl3x">
        {factors.map(factor => <article key={factor.title} className="flex flex-col rounded-xl border border-ink/10 bg-paper p-4" data-id="element-2134" data-mp="3uyoqoxlyk5yy">
            <div className="flex items-start justify-between gap-2" data-id="element-2135" data-mp="3q5lv6jxmemg4">
              <h4 className="text-sm font-semibold" data-id="element-2136" data-mp="32gu6i2j8om83">{factor.title}</h4>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${strengthTone[factor.strength]}`} data-id="element-2137" data-mp="2xiixfu0rqfqx">{factor.strength}</span>
            </div>
            <p className="mt-2 text-xs font-semibold leading-relaxed text-ink/80" data-id="element-2138" data-mp="1cz5qi2d8z3dj">{factor.headline}</p>
            <ul className="mt-2.5 space-y-1.5" data-id="element-2139" data-mp="1ht9xumqfddf5">
              {factor.points.filter(Boolean).map(point => <li key={point} className="flex gap-2 text-xs leading-relaxed text-ink/60" data-id="element-2140" data-mp="13xz79lj7regl">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#BB5E40]" aria-hidden="true" data-id="element-2141" data-mp="17zavpnqcptn7" />
                  {point}
                </li>)}
            </ul>
            <p className="mt-auto border-t border-ink/10 pt-2.5 text-xs leading-relaxed text-ink/55" style={{
          marginTop: '0.875rem'
        }} data-id="element-2142" data-mp="20p9re1z1wp0u">{factor.takeaway}</p>
          </article>)}
      </div>
    </section>;
}
function Composition({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [open, setOpen] = useState(false);
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  return <section className="mt-4 overflow-hidden rounded-xl border border-ink/10 bg-paper" aria-label="Assumptions and composition" data-id="element-2143" data-mp="qswbb66mknyl">
      <button type="button" onClick={() => setOpen(current => !current)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-cream/45" data-id="element-2144" data-mp="lul12ucf4bfu">
        <span data-id="element-2145" data-mp="19j47f4bwqe5a">
          <span className="block text-sm font-semibold text-ink" data-id="element-2146" data-mp="19jduok5ki77a">How the forecast was built</span>
          <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2147" data-mp="15h93rt6i7ooc">
            {perUnit(weightedMsrp)} weighted MSRP · {recoveryRate}% modeled recovery · {coreShare}% core sizes · {count(upload.lineItems)} variant lines
          </span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2148" data-mp="3q66oz0x67mte" />
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-5" data-id="element-2149" data-mp="20syyq6xyhxhg">
          <ol className="grid gap-3 lg:grid-cols-4" data-id="element-2150" data-mp="25pfn9292idv2">
            <WaterfallStep label="Weighted MSRP" value={perUnit(weightedMsrp)} detail="Retail value per unit across the lot." data-id="element-2151" data-mp="25o4l16v9te62" />
            <WaterfallStep label="A-grade promo price" value={perUnit(weightedMsrp * 0.7)} detail="Nisolo was running ~30% off new product." data-id="element-2152" data-mp="2eiakp9san5aa" />
            <WaterfallStep label="Predicted recovery" value={perUnit(outcome.predictedAur)} detail={`${recoveryRate}% of MSRP after condition and demand.`} data-id="element-2153" data-mp="2jh3xezzras9e" />
            <WaterfallStep label="Actual recovery" value={outcome.matched ? perUnit(outcome.actualAur) : '—'} detail={outcome.matched ? `${signedPct(outcome.aurVariancePct)} against the forecast.` : 'Pending first matched sales.'} highlight data-id="element-2154" data-mp="2tg2z1ym3tnmw" />
          </ol>

          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-2155" data-mp="134f2tta08ifd">
            <BreakdownStat label="Variant lines" value={count(upload.lineItems)} detail={`${count(upload.received)} units received`} data-id="element-2156" data-mp="26e9s9yxcr822" />
            <BreakdownStat label="Units per line" value={upload.lineItems > 0 ? (upload.received / upload.lineItems).toFixed(1) : '—'} detail={`${count(upload.lines.filter(line => line.units >= 100).length)} lines carry 100+ units`} data-id="element-2157" data-mp="21g897dzxf8ql" />
            <BreakdownStat label="Modeled recovery" value={`${recoveryRate}%`} detail={`of ${currency(upload.msrp)} submitted MSRP`} data-id="element-2158" data-mp="2bfgyvunre175" />
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-3" data-id="element-2159" data-mp="2keuaasvg0zui">
            <Distribution title="Sizes" items={upload.sizes.slice(0, 8)} total={upload.received} tiered data-id="element-2160" data-mp="2kc81kpwtgeap" />
            <Distribution title="Categories" items={upload.categories.slice(0, 8)} total={upload.received} data-id="element-2161" data-mp="2u3voqemcb96d" />
            <Distribution title="MSRP bands" items={upload.priceBands} total={upload.received} data-id="element-2162" data-mp="383dil8ehtcml" />
          </div>

          <div className="mt-4 grid gap-4 border-t border-ink/10 pt-4 lg:grid-cols-2" data-id="element-2163" data-mp="335276t8juu29">
            <div data-id="element-2164" data-mp="3mqlnxsl4enqn">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2165" data-mp="3mq2hb80x7lrx">Confidence: medium — what we knew</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2166" data-mp="3rowxs3bf0y8v">
                Unit counts, MSRP, size mix, and the {upload.grade} designation were known. {count(upload.lineItems - upload.missingPredict)} of {count(upload.lineItems)} lines carried a per-unit estimate, and {count(upload.shortShipped)} invoiced units never arrived.
              </p>
            </div>
            <div data-id="element-2167" data-mp="38rm9fdrbnp87">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2168" data-mp="38s5hm2r66z91">What we had to estimate</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2169" data-mp="2tzlh79ven5va">
                Demand by style, liquidity by size, and how consistently the {upload.grade} condition would land with customers — the network had no prior Nisolo sell-through to calibrate against.
              </p>
            </div>
          </div>
        </div>}
    </section>;
}
function WaterfallStep({
  label,
  value,
  detail,
  highlight
}: {
  label: string;
  value: string;
  detail: string;
  highlight?: boolean;
}) {
  return <li className={`rounded-xl border p-4 ${highlight ? 'border-[#BB5E40] bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/40'}`} data-id="element-2170" data-mp="30gwxy5ruxlg3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2171" data-mp="2rilbmfy0x9tg">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${highlight ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2172" data-mp="2mj8hda4tp0gu">{value}</p>
      <p className="mt-1.5 text-xs leading-relaxed text-ink/55" data-id="element-2173" data-mp="2hrgvve0ixkxk">{detail}</p>
    </li>;
}
function BreakdownStat({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-xl border border-ink/10 bg-cream/40 px-4 py-3.5" data-id="element-2174" data-mp="27vncpbsaut6t">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2175" data-mp="23t0hr20c0s5e">{label}</p>
      <p className="mt-1.5 font-display text-xl font-semibold tracking-tight text-ink" data-id="element-2176" data-mp="1yte2yo4uv3uh">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2177" data-mp="ec4zxef8gqhd">{detail}</p>
    </article>;
}
function Distribution({
  title,
  items,
  total,
  tiered
}: {
  title: string;
  items: Array<{
    label: string;
    units: number;
    tier?: SizeTier;
  }>;
  total: number;
  tiered?: boolean;
}) {
  const max = Math.max(...items.map(item => item.units), 1);
  const tone: Record<SizeTier, string> = {
    Core: 'bg-[#BB5E40]',
    Adjacent: 'bg-[#C9A04E]',
    Tail: 'bg-ink/25'
  };
  return <article data-id="element-2178" data-mp="3rs2t187cqcjd">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2179" data-mp="3rof2mhx7e68v">{title}</p>
      <ul className="mt-2.5 space-y-1.5" data-id="element-2180" data-mp="3vq0kyisibcqg">
        {items.map(item => <li key={item.label} className="flex items-center gap-2.5" data-id="element-2181" data-mp="4l0kyepk55mj">
            <span className="w-20 shrink-0 truncate text-xs text-ink/65" data-id="element-2182" data-mp="9csaqn0w37bm">{item.label}</span>
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/8" data-id="element-2183" data-mp="38327ltzookwr">
              <span className={`block h-full rounded-full ${tiered ? tone[item.tier ?? 'Tail'] : 'bg-[#BB5E40]'}`} style={{
            width: `${Math.max(2, item.units / max * 100)}%`
          }} data-id="element-2184" data-mp="3d1lubwm3q0q6" />
            </span>
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-ink/55" data-id="element-2185" data-mp="2rqstpqhkciwz">{total > 0 ? Math.round(item.units / total * 100) : 0}%</span>
          </li>)}
      </ul>
    </article>;
}
function buildContext(upload: NisoloSubmission, outcome: ReturnType<typeof reconcileSubmission>, weightedMsrp: number, coreShare: number, instructions: string[]) {
  return [`Inventory submission: "${upload.name}" (${upload.grade}), uploaded ${upload.arrived}, brand Nisolo, sold through the Manymoons recovery network.`, `Received ${upload.received} units across ${upload.lineItems} variant lines. ${upload.shortShipped} invoiced units never arrived.`, `Submitted MSRP value ${currency(upload.msrp)} (${perUnit(weightedMsrp)} weighted per unit). Nisolo A-grade was promoting at ~30% off, so first-quality price sat near ${perUnit(weightedMsrp * 0.7)}.`, `FORECAST: ${currency(upload.estimatedRecovery)} total, ${perUnit(outcome.predictedAur)} per unit received.`, outcome.matched ? `ACTUAL SO FAR: ${outcome.soldUnits} units sold for ${currency(outcome.actualSales)} (${perUnit(outcome.actualAur)} AUR). Those same units were forecast at ${currency(outcome.predictedForSold)}, so variance is ${currency(outcome.variance)} (${signedPct(outcome.variancePct)}). AUR variance ${signedPct(outcome.aurVariancePct)}. Sell-through ${outcome.sellThrough.toFixed(1)}%. ${outcome.unsoldUnits} units remain, carrying ${currency(outcome.remainingPredicted)} of remaining forecast.` : 'ACTUAL SO FAR: no sales have been matched to this upload yet, so there is no realized variance.', outcome.overPerformers.length > 0 ? `Beat forecast: ${outcome.overPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', outcome.underPerformers.length > 0 ? `Missed forecast: ${outcome.underPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', `Composition: ${coreShare}% of units in core-demand sizes. Categories: ${upload.categories.slice(0, 6).map(item => `${item.label} (${item.units})`).join(', ')}. MSRP bands: ${upload.priceBands.map(item => `${item.label} (${item.units})`).join(', ')}.`, '', ...instructions].filter(Boolean).join('\n');
}
function ReportChat({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [messages, setMessages] = useState<Array<{
    id: number;
    role: 'user' | 'assistant';
    text: string;
  }>>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const suggestions = outcome.matched ? ['Why did the forecast miss?', 'What should we reprice?', 'How should we clear what’s left?'] : ['What should launch first?', 'How should we price this?', 'What are the biggest risks?'];
  const send = async (question: string) => {
    if (!question.trim() || thinking) return;
    const history: AgentMessage[] = [...messages.map(message => ({
      role: message.role,
      content: message.text
    })), {
      role: 'user',
      content: question
    }];
    setMessages(current => [...current, {
      id: Date.now(),
      role: 'user',
      text: question
    }]);
    setDraft('');
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Answer only from this submission data. Be specific and commercial. Two short paragraphs maximum.']),
        maxTokens: 600
      });
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: reply
      }]);
    } catch (caught) {
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: (caught as Error).message
      }]);
    } finally {
      setThinking(false);
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void send(draft.trim());
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/30 bg-[#BB5E40]/5 p-4 sm:p-5" aria-label="Ask Carli about this report" data-id="element-2186" data-mp="3zbn7xj9uktb">
      <div className="flex items-center gap-2.5" data-id="element-2187" data-mp="wpk9o1kubrdp">
        <CarliMark data-id="element-2188" data-mp="wnpja7qsv728" />
        <span data-id="element-2189" data-mp="10rfaz62ru4rh">
          <span className="block text-sm font-semibold" data-id="element-2190" data-mp="10rxb4dsk134o">Ask Carli about this upload</span>
          <span className="block text-xs text-ink/55" data-id="element-2191" data-mp="mrwa8ycahvp7">She answers from this submission’s units, pricing, and realized sales.</span>
        </span>
      </div>

      {messages.length > 0 && <ul className="mt-4 max-h-56 space-y-2.5 overflow-y-auto pr-1" style={{
      scrollbarGutter: 'stable'
    }} aria-live="polite" data-id="element-2192" data-mp="37hv8gg3j8qem">
          {messages.map(message => <li key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex gap-2.5'} data-id="element-2193" data-mp="3cevbsj8zzf21">
              {message.role === 'assistant' && <CarliMark compact data-id="element-2194" data-mp="2zyvmq3slmms3" />}
              <p className={`max-w-2xl whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${message.role === 'user' ? 'bg-[#BB5E40] text-cream' : 'bg-paper text-ink/80'}`} data-id="element-2195" data-mp="2zxub9y7o7y9u">{message.text}</p>
            </li>)}
        </ul>}

      {thinking && <p className="mt-3 flex items-center gap-2 text-xs text-[#94452E]" data-id="element-2196" data-mp="2lahmm0mkhcf8">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-2197" data-mp="2h84ej5kcl2fa" />
          Carli is reviewing this upload…
        </p>}

      <form onSubmit={submit} className="mt-3 flex items-center gap-2 rounded-xl border border-[#BB5E40]/30 bg-paper p-1.5 pl-3.5 focus-within:border-[#BB5E40] focus-within:ring-2 focus-within:ring-[#BB5E40]/15" data-id="element-2198" data-mp="2v0u3niv6angm">
        <label htmlFor={`report-chat-${upload.id}`} className="sr-only" data-id="element-2199" data-mp="22gvlsnl5f0xx">Ask Carli about {upload.name}</label>
        <input id={`report-chat-${upload.id}`} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask Carli about this upload…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-2200" data-mp="1xi13rnv09gev" />
        <button type="submit" disabled={!draft.trim() || thinking} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" aria-label="Send question" data-id="element-2201" data-mp="g5n4jhdeg18h">
          {thinking ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-2202" data-mp="3okdkewklvvwf" /> : <SendHorizontalIcon className="h-4 w-4" data-id="element-2203" data-mp="3onsypx6gh0ws" />}
        </button>
      </form>

      {messages.length === 0 && <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-2204" data-mp="13vhenr6c3bp1">
          {suggestions.map(item => <button key={item} type="button" onClick={() => void send(item)} className="rounded-full border border-[#BB5E40]/35 bg-paper px-3 py-1.5 text-xs font-medium text-[#94452E] transition-colors hover:bg-[#BB5E40] hover:text-cream" data-id="element-2205" data-mp="17wj8ttle8xk5">
              {item}
            </button>)}
        </div>}
    </section>;
}
function CarliMark({
  compact = false
}: {
  compact?: boolean;
}) {
  return <span aria-hidden="true" className={`flex shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]/12 ${compact ? 'h-7 w-7' : 'h-9 w-9'}`} data-id="element-2206" data-mp="2elm7sfl50ue6">
      <span className="bg-[#BB5E40]" style={{
      width: compact ? '0.875rem' : '1.125rem',
      height: compact ? '0.875rem' : '1.125rem',
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-2207" data-mp="24o7s4i71g9mx" />
    </span>;
}